import React, { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Clock, Video, MapPin, Globe, User, Calendar, Check, ChevronsUpDown, DollarSign } from 'lucide-react';
import { cn, getTimeZonesList } from '@/lib/utils';
import { format } from 'date-fns';

interface BookingSummaryProps {
  practitionerName: string;
  practitionerCredentials?: string;
  practitionerSpecialization?: string;
  clinicName?: string;
  appointmentTypeName?: string;
  durationMinutes?: number;
  isTelehealth?: boolean;
  selectedDate?: Date;
  selectedTime?: string;
  timezone: string;
  onTimezoneChange?: (tz: string) => void;
  showTimezoneSelector?: boolean;
  priceCents?: number;
  compact?: boolean;
  className?: string;
}

// Helper to get friendly timezone label using Intl
const getTimezoneLabel = (tzValue: string): string => {
  try {
    const abbr = new Intl.DateTimeFormat('en-US', { timeZone: tzValue, timeZoneName: 'short' })
      .formatToParts(new Date())
      .find(p => p.type === 'timeZoneName')?.value || '';
    const cityName = tzValue.split('/').pop()?.replace(/_/g, ' ') || tzValue;
    return `${cityName} (${abbr})`;
  } catch {
    return tzValue;
  }
};

export const BookingSummary: React.FC<BookingSummaryProps> = ({
  practitionerName,
  practitionerCredentials,
  practitionerSpecialization,
  clinicName,
  appointmentTypeName,
  durationMinutes,
  isTelehealth = true,
  selectedDate,
  selectedTime,
  timezone,
  onTimezoneChange,
  priceCents,
  showTimezoneSelector = true,
  compact = false,
  className,
}) => {
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const allTimezones = useMemo(
    () =>
      getTimeZonesList().map(tz => ({
        value: tz.zone,
        label: `${tz.text} (${tz.zone})`,
      })),
    []
  );

  const timezoneOptions = useMemo(() => {
    const currentTzInList = allTimezones.find(tz => tz.value === timezone);
    if (currentTzInList) {
      return allTimezones;
    }
    return [
      { value: timezone, label: getTimezoneLabel(timezone) },
      ...allTimezones,
    ];
  }, [timezone, allTimezones]);

  const formattedDate = useMemo(() => {
    if (!selectedDate) return null;
    return format(selectedDate, 'EEEE, MMMM d, yyyy');
  }, [selectedDate]);

  const formattedDateShort = useMemo(() => {
    if (!selectedDate) return null;
    return format(selectedDate, 'EEE, MMM d');
  }, [selectedDate]);

  if (compact) {
    return (
      <div className={cn('rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4', className)}>
        <div className='flex items-center gap-3'>
          <div className='flex h-10 w-10 items-center justify-center rounded-full bg-teal-100 flex-shrink-0'>
            <User className='h-5 w-5 text-teal-600' />
          </div>
          <div className='min-w-0 flex-1'>
            <p className='font-semibold text-slate-900 truncate'>
              {practitionerName}
              {practitionerCredentials && (
                <span className='font-normal text-slate-500'>, {practitionerCredentials}</span>
              )}
            </p>
            <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-0.5'>
              {clinicName && <span>{clinicName}</span>}
              {appointmentTypeName && <span>{appointmentTypeName}</span>}
              {durationMinutes && (
                <span className='flex items-center gap-1'>
                  <Clock className='h-3 w-3' /> {durationMinutes} min
                </span>
              )}
              {isTelehealth !== undefined && (
                <span className='flex items-center gap-1'>
                  {isTelehealth ? <Video className='h-3 w-3 text-teal-500' /> : <MapPin className='h-3 w-3 text-blue-500' />}
                  {isTelehealth ? 'Video' : 'In-person'}
                </span>
              )}
              {priceCents != null && priceCents > 0 ? (
                <Badge className='bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] px-1.5 py-0'>
                  ${(priceCents / 100).toFixed(2)}
                </Badge>
              ) : priceCents != null ? (
                <Badge variant='outline' className='text-slate-500 text-[10px] px-1.5 py-0'>Free</Badge>
              ) : null}
            </div>
          </div>
        </div>

        {(selectedDate || selectedTime) && (
          <div className='mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-700 border-t border-slate-200 pt-2'>
            {formattedDateShort && (
              <span className='flex items-center gap-1 font-medium'>
                <Calendar className='h-3 w-3 text-teal-600' /> {formattedDateShort}
              </span>
            )}
            {selectedTime && (
              <span className='flex items-center gap-1 font-medium'>
                <Clock className='h-3 w-3 text-teal-600' /> {selectedTime}
              </span>
            )}
          </div>
        )}

        {showTimezoneSelector && onTimezoneChange && (
          <div className='mt-2 flex items-center gap-2 text-xs text-slate-500 border-t border-slate-200 pt-2'>
            <Globe className='h-3 w-3' />
            <Popover open={timezoneOpen} onOpenChange={setTimezoneOpen}>
              <PopoverTrigger asChild>
                <button type='button' className='text-xs text-slate-600 underline decoration-dotted hover:text-slate-900'>
                  {getTimezoneLabel(timezone)}
                </button>
              </PopoverTrigger>
              <PopoverContent className='w-72 p-0' align='start'>
                <Command>
                  <CommandInput placeholder='Search timezone...' />
                  <CommandList>
                    <CommandEmpty>No timezone found.</CommandEmpty>
                    <CommandGroup className='max-h-48 overflow-auto'>
                      {timezoneOptions.map(tz => (
                        <CommandItem
                          key={tz.value}
                          value={tz.label}
                          onSelect={() => {
                            onTimezoneChange(tz.value);
                            setTimezoneOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-3 w-3',
                              timezone === tz.value ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <span className='text-xs'>{tz.label}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Practitioner Info */}
      <div>
        <div className='mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-teal-100'>
          <User className='h-7 w-7 text-teal-600' />
        </div>

        <h2 className='text-lg font-semibold text-slate-900'>
          {practitionerName}
          {practitionerCredentials && (
            <span className='font-normal text-slate-500'>, {practitionerCredentials}</span>
          )}
        </h2>

        {practitionerSpecialization && (
          <p className='mt-0.5 text-sm text-slate-500'>{practitionerSpecialization}</p>
        )}

        {clinicName && <p className='text-sm text-slate-500'>{clinicName}</p>}
      </div>

      {/* Appointment Type */}
      {appointmentTypeName && (
        <div className='space-y-2 rounded-xl bg-slate-100/70 p-3.5'>
          <h3 className='text-sm font-medium text-slate-900'>{appointmentTypeName}</h3>

          <div className='flex flex-wrap gap-3 text-sm text-slate-600'>
            {durationMinutes && (
              <div className='flex items-center gap-1'>
                <Clock className='h-3.5 w-3.5 text-slate-400' />
                <span>{durationMinutes} min</span>
              </div>
            )}

            {isTelehealth !== undefined && (
              <div className='flex items-center gap-1'>
                {isTelehealth ? (
                  <>
                    <Video className='h-3.5 w-3.5 text-teal-500' />
                    <span>Video call</span>
                  </>
                ) : (
                  <>
                    <MapPin className='h-3.5 w-3.5 text-blue-500' />
                    <span>In-person</span>
                  </>
                )}
              </div>
            )}
            {priceCents != null && priceCents > 0 ? (
              <Badge className='bg-emerald-100 text-emerald-700 border-emerald-200'>
                ${(priceCents / 100).toFixed(2)}
              </Badge>
            ) : priceCents != null ? (
              <Badge variant='outline' className='text-slate-500'>Free</Badge>
            ) : null}
          </div>
        </div>
      )}

      {/* Selected Date & Time */}
      {(selectedDate || selectedTime) && (
        <div className='space-y-1.5 rounded-xl bg-teal-50 p-3.5'>
          {formattedDate && (
            <div className='flex items-center gap-2 text-sm text-slate-700'>
              <Calendar className='h-4 w-4 text-teal-600' />
              <span className='font-medium'>{formattedDate}</span>
            </div>
          )}
          {selectedTime && (
            <div className='flex items-center gap-2 text-sm text-slate-700'>
              <Clock className='h-4 w-4 text-teal-600' />
              <span className='font-medium'>{selectedTime}</span>
            </div>
          )}
        </div>
      )}

      {/* Timezone Selector */}
      {showTimezoneSelector && onTimezoneChange && (
        <div className='border-t border-slate-200 pt-3'>
          <div className='mb-1.5 flex items-center gap-2 text-sm text-slate-500'>
            <Globe className='h-4 w-4' />
            <span>Timezone</span>
          </div>
          <Popover open={timezoneOpen} onOpenChange={setTimezoneOpen}>
            <PopoverTrigger asChild>
              <Button variant='outline' role='combobox' className='w-full justify-between text-xs h-9'>
                {getTimezoneLabel(timezone)}
                <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-full p-0' align='start'>
              <Command>
                <CommandInput placeholder='Search timezone...' />
                <CommandList>
                  <CommandEmpty>No timezone found.</CommandEmpty>
                  <CommandGroup className='max-h-64 overflow-auto'>
                    {timezoneOptions.map(tz => (
                      <CommandItem
                        key={tz.value}
                        value={tz.label}
                        onSelect={() => {
                          onTimezoneChange(tz.value);
                          setTimezoneOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            timezone === tz.value ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        {tz.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
};

export default BookingSummary;
